import React, { useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@roger/ui-components';
import { AlertTriangle, Check, Loader2, Undo2 } from 'lucide-react';

import { settleInvoiceDifferenceFaxNumberPortsPortOrderIdInvoiceDifferencesFieldPost as settleDifference } from '../../../services/api/sdk.gen';
import type {
  InvoiceDifferenceResponse,
  PortOrderDetail,
  SettleDifferenceRequest,
} from '../../../services/api/types.gen';
import { describeError, displayPhone, logFailure, type Notify } from './shared';

const ACCOUNT_NUMBER = 'account_number';
const BILLING_PHONE_NUMBER = 'billing_phone_number';

/**
 * Where a later invoice disagrees with the details entered. Each row is
 * settled by taking the invoice's value or keeping the entered one; submit
 * waits until none is pending. Roger never keeps the account number, so
 * using the invoice's one means typing it from the invoice.
 */
const InvoiceDifferences: React.FC<{
  detail: PortOrderDetail;
  editable: boolean;
  onChanged: (detail: PortOrderDetail) => void;
  showError: Notify;
}> = ({ detail, editable, onChanged, showError }) => {
  const [busy, setBusy] = useState<string | null>(null);
  const [typingAccount, setTypingAccount] = useState(false);
  const [account, setAccount] = useState('');

  const differences = detail.invoice_differences;
  if (differences.length === 0) return null;

  const shown = (difference: InvoiceDifferenceResponse, value: string) =>
    difference.field === BILLING_PHONE_NUMBER ? displayPhone(value) : value;

  const settle = async (
    difference: InvoiceDifferenceResponse,
    body: SettleDifferenceRequest
  ) => {
    setBusy(difference.field);
    try {
      const { data } = await settleDifference({
        path: { port_order_id: detail.id, field: difference.field },
        body,
        throwOnError: true,
      });
      if (difference.field === ACCOUNT_NUMBER) {
        setTypingAccount(false);
        setAccount('');
      }
      onChanged(data);
    } catch (error) {
      logFailure('Failed to settle an invoice difference', error);
      showError(describeError(error, 'Unable to save your choice.'));
    } finally {
      setBusy(null);
    }
  };

  const actions = (difference: InvoiceDifferenceResponse) => {
    const isBusy = busy === difference.field;
    if (difference.resolution === 'kept') {
      return (
        <div className='flex flex-wrap items-center gap-2'>
          <span className='flex items-center gap-1 whitespace-nowrap text-muted-foreground'>
            <Check className='h-4 w-4' />
            Kept yours
          </span>
          {editable && (
            <Button
              variant='ghost'
              size='sm'
              disabled={busy !== null}
              onClick={() => void settle(difference, { action: 'undo' })}
            >
              <Undo2 className='mr-2 h-4 w-4' />
              Undo
            </Button>
          )}
        </div>
      );
    }
    if (!editable) return null;
    if (difference.field === ACCOUNT_NUMBER && typingAccount) {
      return (
        <div className='flex flex-wrap items-center gap-2'>
          <Input
            aria-label='Account number on the invoice'
            autoComplete='off'
            className='w-full sm:w-40'
            value={account}
            disabled={isBusy}
            onChange={event => setAccount(event.target.value)}
          />
          <Button
            size='sm'
            disabled={busy !== null || !account.trim()}
            onClick={() =>
              void settle(difference, {
                action: 'use_invoice',
                account_number: account.trim(),
              })
            }
          >
            {isBusy && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
            Use
          </Button>
          <Button
            variant='ghost'
            size='sm'
            disabled={isBusy}
            onClick={() => {
              setTypingAccount(false);
              setAccount('');
            }}
          >
            Cancel
          </Button>
        </div>
      );
    }
    return (
      <div className='flex flex-wrap gap-2'>
        <Button
          size='sm'
          disabled={busy !== null}
          onClick={() =>
            difference.field === ACCOUNT_NUMBER
              ? setTypingAccount(true)
              : void settle(difference, { action: 'use_invoice' })
          }
        >
          {isBusy && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
          Use invoice
        </Button>
        <Button
          variant='outline'
          size='sm'
          disabled={busy !== null}
          onClick={() => void settle(difference, { action: 'keep_mine' })}
        >
          Keep mine
        </Button>
      </div>
    );
  };

  return (
    <section className='space-y-3'>
      <Alert className='border-amber-300 bg-amber-50 text-amber-800'>
        <AlertTriangle className='h-4 w-4' />
        <AlertTitle>
          Your invoice differs from {differences.length} detail
          {differences.length === 1 ? '' : 's'}
        </AlertTitle>
        <AlertDescription>
          Carriers reject a port when these differ. Pick the right value for
          each.
        </AlertDescription>
      </Alert>
      <div className='overflow-x-auto rounded-md border'>
        <Table className='min-w-[640px]'>
          <TableHeader>
            <TableRow>
              <TableHead>Field</TableHead>
              <TableHead>You entered</TableHead>
              <TableHead>Invoice</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {differences.map(difference => (
              <TableRow key={difference.field}>
                <TableCell className='font-medium'>
                  {difference.label}
                </TableCell>
                <TableCell>
                  {difference.entered
                    ? shown(difference, difference.entered)
                    : 'Not entered'}
                </TableCell>
                <TableCell>
                  {difference.invoice
                    ? shown(difference, difference.invoice)
                    : 'From your invoice'}
                </TableCell>
                <TableCell>{actions(difference)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <p className='text-xs text-muted-foreground'>
        Spacing, case and punctuation are ignored. Signer, PIN and suite are not
        on invoices, so they are not compared.
      </p>
    </section>
  );
};

export default InvoiceDifferences;
